import React from 'react'
import { Check, Minus, ShieldAlert } from 'lucide-react'

// ======================================================================
// WHAT A ROLE GRANTS, AND WHAT IT DOES NOT
// ======================================================================
//
// EVERY SENTENCE HERE COMES FROM THE SERVER. There is no wording in this
// file about what any role can do, and there must never be: the strings
// are generated by er_cases/acl.describe_role() from acl.CAPABILITIES —
// the same frozenset the ACL actually enforces — and served by
// GET /api/cases/case-roles.
//
// A paragraph per role written in a component would be a second copy of
// the access model, one network hop from the first, drifting silently.
// Task 5A found exactly that shape server-side (BROAD_ALLEGATION_ROLES,
// a hand-kept list of who may read allegations) already disagreeing with
// the map it mirrored. Here it would be worse: the drift would be on
// the one screen whose entire job is to tell somebody what they are
// about to grant.
//
// ======================================================================
// BOTH HALVES, AND THE SECOND ONE IS THE POINT
// ======================================================================
//
// "Reads the record of their own interview" is useful. "Cannot read
// what is alleged, cannot see the evidence, cannot see who else is
// involved, cannot see the outcome" is what stops somebody adding a
// witness when they meant an investigator — which is the actual mistake
// this component exists to prevent, and it is a mistake that is
// invisible until the person tells you they cannot see the case.
//
// So the withheld list is rendered at the same weight as the granted
// one, not folded away behind a disclosure triangle. It is longer for
// the roles where it matters most: a witness holds one capability and
// is refused sixteen.

export default function RoleEffect({ description, compact = false }) {
  if (!description) return null
  const { granted, withheld, universal, separation } = description

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <List
        tone="grant"
        title="Can"
        items={[...granted, ...(universal || [])]}
        empty="Nothing beyond declaring a conflict about their own involvement."
      />
      <List
        tone="withhold"
        title="Cannot"
        items={withheld}
        empty="Nothing is withheld from this role."
      />

      {/* THE DELIBERATE ABSENCE, WHERE THERE IS ONE.
          Three roles have one, and they are the separation of function
          the whole module rests on — an investigator does not decide, a
          case owner does not decide, a decision maker does not write the
          notes they decide from. It reads differently from the rest of
          the withheld list because it IS different: those are things the
          role happens not to need, this is a thing it is deliberately
          kept away from, and the person granting roles is exactly the
          person who has to understand which is which.

          acl.SEPARATIONS is asserted against acl.CAPABILITIES at import,
          so this text cannot drift: granting one of them stops the
          backend starting rather than quietly making this box false. */}
      {separation && (
        <div className="rounded-md border border-amber-line bg-amber-dim px-3.5 py-3">
          <div className="flex items-start gap-2.5">
            <ShieldAlert
              size={15}
              strokeWidth={1.9}
              className="mt-0.5 shrink-0 text-amber"
            />
            <div className="min-w-0">
              <p className="font-head text-[12px] font-semibold text-amber">
                Deliberately kept apart
              </p>
              <p className="measure mt-1 text-[12px] leading-relaxed text-muted">
                {separation.reason}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function List({ tone, title, items, empty }) {
  const grant = tone === 'grant'
  const Icon = grant ? Check : Minus
  return (
    <div>
      <div className="eyebrow mb-1.5">{title}</div>
      {items && items.length ? (
        <ul className="space-y-1">
          {items.map((i) => (
            <li key={i.capability} className="flex items-start gap-2">
              <Icon
                size={13}
                strokeWidth={2.1}
                className={`mt-[3px] shrink-0 ${grant ? 'text-accent' : 'text-faint'}`}
              />
              <span
                className={`measure text-[12.5px] leading-relaxed ${
                  grant ? 'text-ink' : 'text-muted'
                }`}
              >
                {i.text}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] leading-relaxed text-faint">{empty}</p>
      )}
    </div>
  )
}
